import React, { useState, useEffect } from 'react';
import { Shield, Check, X } from 'lucide-react';
import GlassCard from '../../../components/shared/GlassCard';

const PermissionsMatrix = () => {
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPermissionsMatrix();
  }, []);

  const fetchPermissionsMatrix = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/users/permissions/matrix`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin'
        }
      });
      const data = await response.json();
      setMatrix(data.matrix || {});
    } catch (error) {
      console.error('Error fetching permissions matrix:', error);
    } finally {
      setLoading(false);
    }
  };

  const roleColors = {
    super_admin: '#570683',
    program_admin: '#417E46',
    noc_operator: '#0DA34E',
    fleet_manager: '#DF8C4D',
    finance_viewer: '#4A90E2',
    gov_viewer: '#9B59B6',
    internal: '#7F8C8D',
    guest: '#95A5A6'
  };

  const hasPermission = (permissions, resource, action) => {
    // Check for wildcard permission
    const wildcardPerm = permissions.find(p => p.resource === '*' && p.actions.includes('*'));
    if (wildcardPerm) return true;

    // Check for specific permission
    const perm = permissions.find(p => p.resource === resource);
    if (!perm) return false;

    return perm.actions.includes('*') || perm.actions.includes(action);
  };

  const resources = [
    { key: 'users', label: 'User Management' },
    { key: 'programs', label: 'Programs' },
    { key: 'workbook', label: 'Master Workbook' },
    { key: 'governance', label: 'Governance' },
    { key: 'stations', label: 'Stations' },
    { key: 'network_ops', label: 'Network Operations' },
    { key: 'incidents', label: 'Incidents' },
    { key: 'alerts', label: 'Alerts' },
    { key: 'fleet', label: 'Fleet Management' },
    { key: 'sessions', label: 'Charging Sessions' },
    { key: 'vehicles', label: 'Vehicles' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'financial_reports', label: 'Financial Reports' },
    { key: 'gov_connect', label: 'Gov Connect' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'reports', label: 'Reports' },
    { key: 'dashboard', label: 'Dashboard' }
  ];

  const actions = ['read', 'write', 'delete', 'approve', 'export'];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text)' }}>
          Permissions Matrix
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Role-based access control (RBAC) permissions overview
        </p>
      </div>

      {loading ? (
        <GlassCard>
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-transparent"></div>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>Loading permissions...</p>
          </div>
        </GlassCard>
      ) : (
        <>
          {/* Roles Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {Object.entries(matrix).map(([roleKey, roleData]) => (
              <GlassCard key={roleKey} className="p-4">
                <div className="flex items-start gap-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${roleColors[roleKey]}15` }}
                  >
                    <Shield className="w-5 h-5" style={{ color: roleColors[roleKey] }} />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1" style={{ color: 'var(--text)' }}>
                      {roleData.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="text-xs mb-2" style={{ color: 'var(--muted)' }}>
                      {roleData.description}
                    </div>
                    <div className="text-xs font-medium" style={{ color: roleColors[roleKey] }}>
                      → {roleData.default_dashboard}
                    </div>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>

          {/* Detailed Permissions Table */}
          <GlassCard>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--panel-border)' }}>
                    <th className="text-left py-3 px-4 font-semibold text-sm sticky left-0 bg-white z-10" style={{ color: 'var(--text)' }}>
                      Resource
                    </th>
                    {Object.entries(matrix).map(([roleKey, roleData]) => (
                      <th key={roleKey} className="text-center py-3 px-2 font-semibold text-xs" style={{ color: roleColors[roleKey] }}>
                        <div className="flex flex-col items-center gap-1">
                          <Shield className="w-4 h-4" />
                          <span>{roleData.role.replace('_', ' ').split(' ').map(w => w[0].toUpperCase()).join('')}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resources.map((resource, idx) => (
                    <React.Fragment key={resource.key}>
                      <tr className="border-b" style={{ borderColor: 'var(--panel-border)' }}>
                        <td 
                          colSpan={Object.keys(matrix).length + 1}
                          className="py-2 px-4 font-semibold text-sm"
                          style={{ 
                            backgroundColor: 'var(--panel-bg)',
                            color: 'var(--text)'
                          }}
                        >
                          {resource.label}
                        </td>
                      </tr>
                      {actions.map((action) => (
                        <tr 
                          key={`${resource.key}-${action}`}
                          className="border-b hover:bg-gray-50/50"
                          style={{ borderColor: 'var(--panel-border)' }}
                        >
                          <td className="py-2 px-4 pl-8 text-sm sticky left-0 bg-white" style={{ color: 'var(--muted)' }}>
                            {action.charAt(0).toUpperCase() + action.slice(1)}
                          </td>
                          {Object.entries(matrix).map(([roleKey, roleData]) => (
                            <td key={roleKey} className="py-2 px-2 text-center">
                              {hasPermission(roleData.permissions, resource.key, action) ? (
                                <Check className="w-4 h-4 mx-auto" style={{ color: '#0DA34E' }} />
                              ) : (
                                <X className="w-4 h-4 mx-auto opacity-20" style={{ color: '#95A5A6' }} />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>

          {/* Legend */}
          <div className="mt-6 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" style={{ color: '#0DA34E' }} />
              <span style={{ color: 'var(--text)' }}>Permission Granted</span>
            </div>
            <div className="flex items-center gap-2">
              <X className="w-4 h-4 opacity-20" style={{ color: '#95A5A6' }} />
              <span style={{ color: 'var(--text)' }}>Permission Denied</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" style={{ color: '#570683' }} />
              <span style={{ color: 'var(--text)' }}>SA = Super Admin</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionsMatrix;
